// Types
import type {
  Image,
  ImageVariant,
} from '../../../server/src/types/media.types';

/**
 * Get image URL based on variant type or fallback to original URL
 * @param image - The image object
 * @param variantType - The desired variant type (THUMBNAIL, PHONE, TABLET, DESKTOP)
 * @param fallbackToOriginal - Whether to fallback to original URL if variant not found (default: true)
 * @returns The image URL or empty string if not found
 */
export const getImageUrl = (
  image: Image,
  variantType?: ImageVariant['variantType'],
  fallbackToOriginal: boolean = true
): string => {
  // If no variant type specified and fallback is allowed, return original URL
  if (!variantType && fallbackToOriginal) {
    return image.url || '';
  }

  // If no variants available
  if (!image.variants || image.variants.length === 0) {
    return fallbackToOriginal ? image.url || '' : '';
  }

  // Find the specific variant
  if (variantType) {
    const variant = image.variants.find((v) => v.variantType === variantType);
    if (variant) {
      return variant.url;
    }
  }

  // Fallback logic
  if (fallbackToOriginal) {
    // First try to return original URL
    if (image.url) {
      return image.url;
    }
    // If no original URL, return first available variant
    if (image.variants.length > 0) {
      return image.variants[0].url;
    }
  }

  return '';
};

/**
 * Get the best available image URL based on screen size or use case
 * @param image - The image object
 * @param preferredSize - Preferred size (small, medium, large)
 * @returns The best matching image URL
 */
export const getBestImageUrl = (
  image: Image,
  preferredSize: 'small' | 'medium' | 'large' = 'medium'
): string => {
  const sizeMapping = {
    small: ['THUMBNAIL', 'PHONE', 'TABLET', 'DESKTOP'],
    medium: ['PHONE', 'TABLET', 'THUMBNAIL', 'DESKTOP'],
    large: ['DESKTOP', 'TABLET', 'PHONE', 'THUMBNAIL'],
  } as const;

  const priorityOrder = sizeMapping[preferredSize];

  for (const variantType of priorityOrder) {
    const url = getImageUrl(image, variantType, false);
    if (url) {
      return url;
    }
  }

  // Ultimate fallback to original URL
  return image.url || '';
};
